import { useEffect, useRef, useCallback, useState } from 'react';
import { STORAGE } from '../constants';
import {
  getMapIndex,
  loadMap,
  saveMap,
  createMap,
  deleteMap as deleteMapFromStorage,
  renameMap as renameMapInStorage,
  getSettings,
  setSettings,
  exportMapData,
  importMapData,
  normalizeNodeFlashcard
} from '../utils/storage';
import { pushMap, deleteMapFromCloud } from '../utils/mapSync';

const sanitizeString = (v) => (typeof v === 'string' ? v.slice(0, 5000) : '');

function validateMapJSON(data) {
  if (!data || typeof data !== 'object') throw new Error('Formato non valido');
  if (!Array.isArray(data.nodes)) throw new Error('nodes deve essere un array');
  if (!Array.isArray(data.connections)) throw new Error('connections deve essere un array');

  for (const n of data.nodes) {
    if (!n || typeof n !== 'object') throw new Error('Nodo non valido');
    if (typeof n.id !== 'string' && typeof n.id !== 'number') throw new Error('Nodo senza id');
    if (typeof n.x !== 'number' || typeof n.y !== 'number') throw new Error(`Nodo ${n.id}: coordinate non valide`);
    // Sanitize text fields in-place
    n.title = sanitizeString(n.title);
    if (n.summary !== undefined) n.summary = sanitizeString(n.summary);
    if (n.description !== undefined) n.description = sanitizeString(n.description);
  }

  for (const c of data.connections) {
    if (!c || typeof c !== 'object') throw new Error('Connessione non valida');
    if (c.from === undefined || c.to === undefined) throw new Error('Connessione senza from/to');
  }
}

export const usePersistence = ({ nodes, connections, messages, postIts, reports, currentMode, mapContext }, { setNodes, setConnections, setMessages, setPostIts, setReports, setCurrentMode, setMapContext }, user = null) => {
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);
  const [currentMapId, setCurrentMapId] = useState(() => {
    const index = getMapIndex();
    const settings = getSettings();

    if (index.length === 0) {
      const firstMap = createMap('La mia prima mappa');
      setSettings({ lastMapId: firstMap.id });
      return firstMap.id;
    } else {
      const targetId = settings.lastMapId && index.find(m => m.id === settings.lastMapId)
        ? settings.lastMapId
        : index[0].id;
      return targetId;
    }
  });
  
  const [maps, setMaps] = useState(() => {
    const index = getMapIndex();
    return index.length === 0 ? [{ id: currentMapId, name: 'La mia prima mappa', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }] : index;
  });
  
  const debounceTimer = useRef(null);
  const isHydrating = useRef(false);
  const isInitialized = useRef(false);

  const hydrateFromMap = useCallback((mapId) => {
    isHydrating.current = true;
    const data = loadMap(mapId);
    if (data) {
      setNodes((data.nodes || []).map(normalizeNodeFlashcard));
      setConnections(data.connections || []);
      setMessages(data.messages || []);
      setPostIts(data.postIts || []);
      setReports(data.reports || []);
      setCurrentMode(data.mode || 'brainstormer');
      setMapContext(data.mapContext || '');
    } else {
      setNodes([]);
      setConnections([]);
      setMessages([]);
      setPostIts([]);
      setReports([]);
      setCurrentMode('brainstormer');
      setMapContext('');
    }
    setTimeout(() => { isHydrating.current = false; }, 100);
  }, [setNodes, setConnections, setMessages, setPostIts, setReports, setCurrentMode, setMapContext]);

  useEffect(() => {
    if (!isInitialized.current && currentMapId) {
      hydrateFromMap(currentMapId);
      isInitialized.current = true;
    }
  }, [currentMapId, hydrateFromMap]);

  useEffect(() => {
    if (!currentMapId || isHydrating.current) return;

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      saveMap(currentMapId, { nodes: nodes.map(n => normalizeNodeFlashcard({ ...n, _hidden: undefined })), connections, messages, postIts, reports, mode: currentMode, mapContext });
      setMaps(getMapIndex());
      if (userRef.current) {
        pushMap(currentMapId, userRef.current.id).catch(console.error);
      }
    }, STORAGE.AUTOSAVE_DEBOUNCE_MS);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [nodes, connections, messages, postIts, reports, currentMapId, currentMode, mapContext]);

  const switchMap = useCallback((mapId) => {
    if (mapId === currentMapId) return;

    if (currentMapId) {
      saveMap(currentMapId, { nodes: nodes.map(normalizeNodeFlashcard), connections, messages, postIts, reports, mode: currentMode, mapContext });
      if (userRef.current) {
        pushMap(currentMapId, userRef.current.id).catch(err => console.error('Sync error on switch:', err));
      }
    }

    hydrateFromMap(mapId);
    setCurrentMapId(mapId);
    setSettings({ lastMapId: mapId });
  }, [currentMapId, nodes, connections, messages, postIts, reports, currentMode, mapContext, hydrateFromMap]);

  const createNewMap = useCallback((name = STORAGE.DEFAULT_MAP_NAME) => {
    if (currentMapId) {
      saveMap(currentMapId, { nodes: nodes.map(normalizeNodeFlashcard), connections, messages, postIts, reports, mode: currentMode, mapContext });
      if (userRef.current) {
        pushMap(currentMapId, userRef.current.id).catch(err => console.error('Sync error on create:', err));
      }
    }

    const newMap = createMap(name);
    setMaps(getMapIndex());
    
    hydrateFromMap(newMap.id);
    setCurrentMapId(newMap.id);
    setSettings({ lastMapId: newMap.id });

    return newMap;
  }, [currentMapId, nodes, connections, messages, postIts, reports, currentMode, mapContext, hydrateFromMap]);

  const deleteMapById = useCallback((mapId) => {
    const index = getMapIndex();
    const meta = index.find(m => m.id === mapId);

    deleteMapFromStorage(mapId);
    const updatedIndex = getMapIndex();
    setMaps(updatedIndex);

    if (userRef.current && meta?.supabase_id) {
      deleteMapFromCloud(meta.supabase_id).catch(err => console.error('Sync error on delete:', err));
    }

    if (mapId === currentMapId) {
      if (updatedIndex.length === 0) {
        const newMap = createMap('La mia prima mappa');
        setMaps(getMapIndex());
        hydrateFromMap(newMap.id);
        setCurrentMapId(newMap.id);
        setSettings({ lastMapId: newMap.id });
      } else {
        const nextMap = updatedIndex[0];
        hydrateFromMap(nextMap.id);
        setCurrentMapId(nextMap.id);
        setSettings({ lastMapId: nextMap.id });
      }
    }
  }, [currentMapId, hydrateFromMap]);

  const renameCurrentMap = useCallback((name) => {
    if (!currentMapId) return;
    renameMapInStorage(currentMapId, name);
    setMaps(getMapIndex());
    if (userRef.current) {
      pushMap(currentMapId, userRef.current.id).catch(err => console.error('Sync error on rename:', err));
    }
  }, [currentMapId]);

  const exportCurrentMap = useCallback(() => {
    if (!currentMapId) return;
    const data = exportMapData(currentMapId);
    if (!data) return;

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${data.name.replace(/[^a-zA-Z0-9-_ ]/g, '')}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [currentMapId]);

  const importMap = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = JSON.parse(e.target.result);
          validateMapJSON(data);
          const meta = importMapData(data);
          setMaps(getMapIndex());

          hydrateFromMap(meta.id);
          setCurrentMapId(meta.id);
          setSettings({ lastMapId: meta.id });

          if (userRef.current) {
            pushMap(meta.id, userRef.current.id).catch(err => console.error('Sync error on import:', err));
          }

          resolve(meta);
        } catch (err) {
          reject(new Error(`Errore importazione: ${err.message}`));
        }
      };
      reader.onerror = () => reject(new Error('Errore lettura file'));
      reader.readAsText(file);
    });
  }, [hydrateFromMap]);

  const saveReport = useCallback((reportData) => {
    const createdAt = reportData?._createdAt || new Date().toISOString();
    const entry = {
      id: `report-${Date.now()}`,
      createdAt,
      data: reportData,
    };
    setReports(prev => [entry, ...prev]);
  }, [setReports]);

  const deleteReport = useCallback((reportId) => {
    setReports(prev => prev.filter(r => r.id !== reportId));
  }, [setReports]);

  const currentMapName = maps.find(m => m.id === currentMapId)?.name || '';

  const refreshMaps = useCallback(() => {
    setMaps(getMapIndex());
  }, []);

  const reloadCurrentMap = useCallback(() => {
    hydrateFromMap(currentMapId);
  }, [currentMapId, hydrateFromMap]);

  return {
    currentMapId,
    currentMapName,
    maps,
    switchMap,
    createNewMap,
    deleteMapById,
    renameCurrentMap,
    exportCurrentMap,
    importMap,
    saveReport,
    deleteReport,
    refreshMaps,
    reloadCurrentMap,
  };
};
